/**
 * LocalModel
 *
 * Drives Wavebox's on-device AI (the standard web-platform Prompt API,
 * `window.LanguageModel`) for the "On-device" engine. Structurally a sibling of
 * BrainboxTools: the server pushes work down, this hook runs it in-page and
 * pushes results back up.
 *
 * Three responsibilities:
 *   1. Probe capability on mount (and after a download) so the engine picker
 *      knows whether to offer the on-device option.
 *   2. On `run_local_model`, generate the reply on-device and relay tokens back
 *      to the StreamCoordinator via `local_token` → `local_done`/`local_error`.
 *   3. On `abort_local`, stop an in-flight generation.
 *
 * The reply is generated only on the tab that initiated the send (the server
 * pushes `run_local_model` to that LiveView alone); all tabs still render via
 * the coordinator's broadcast.
 */

// Split reasoning models' <think>…</think> spans out of the visible answer.
// Streams arrive as deltas, so we track whether we're currently inside a think
// block across chunks. Returns { visible, done } for the delta.
function makeThinkFilter () {
  let inThink = false
  let carry = ''
  return function feed (delta) {
    let text = carry + delta
    carry = ''
    let visible = ''
    while (text.length > 0) {
      if (inThink) {
        const close = text.indexOf('</think>')
        if (close === -1) {
          // Keep a small tail in case the closing tag is split across chunks.
          carry = text.slice(-8)
          text = ''
        } else {
          text = text.slice(close + '</think>'.length)
          inThink = false
        }
      } else {
        const open = text.indexOf('<think>')
        if (open === -1) {
          const tail = text.slice(-7)
          if (/^<think?$|^<thin?$|^<thi?$|^<th?$|^<t?$|^<?$/.test(tail)) {
            carry = tail
            visible += text.slice(0, text.length - tail.length)
          } else {
            visible += text
          }
          text = ''
        } else {
          visible += text.slice(0, open)
          text = text.slice(open + '<think>'.length)
          inThink = true
        }
      }
    }
    return visible
  }
}

// Token headroom reserved for the model's own reply when checking if input fits.
const OUTPUT_RESERVE_TOKENS = 1024
// Conservative fallback input ceiling when the session quota API is unavailable.
// Small on purpose — on-device windows can be ~1–4k tokens; the map-reduce
// splitter refines from here using real measurements.
const FALLBACK_INPUT_CHARS = 4000
// Initial map-chunk size before per-chunk measured splitting kicks in.
const CHUNK_CHARS = 6000
// Don't split a chunk below this — if even this won't fit, the window is tiny;
// we skip the fragment rather than loop forever.
const MIN_CHUNK_CHARS = 500
// Safety cap on map chunks so a pathologically long page can't spawn 100s of
// serialized on-device calls. Beyond this we note the truncation to the user.
const MAX_CHUNKS = 40

// Remove <think>…</think> spans from a complete (non-streamed) string.
function stripThink (text) {
  return String(text).replace(/<think>[\s\S]*?<\/think>/g, '').trim()
}

// Human-friendly token count for the progress line ("450 tokens", "1.2k tokens").
function formatTokens (n) {
  const t = Math.max(0, Math.round(n || 0))
  return t < 1000 ? `${t} tokens` : `${(t / 1000).toFixed(1)}k tokens`
}

// Playful, rotating verbs for the map-reduce progress line ("Pondering… 1.2k
// tokens"). Cycled by chunk index so consecutive parts read differently.
const PONDER_VERBS = [
  'Pondering',
  'Theorizing',
  'Digesting',
  'Untangling',
  'Mulling over',
  'Absorbing',
  'Contemplating',
  'Distilling',
  'Chewing on',
  'Deliberating on'
]

const LocalModel = {
  mounted () {
    this.controller = null
    this.session = null
    this.downloading = false
    this._ticker = null

    this.handleEvent('run_local_model', (data) => this.run(data))
    this.handleEvent('abort_local', () => this.abort())
    // Fired when the user selects the on-device engine — start the model
    // download immediately rather than waiting for the first send.
    this.handleEvent('prepare_local_model', () => this.ensureDownloaded())

    this.probe()
  },

  // Brainbox reconnects the LiveSocket after auth; the reconnected LiveView
  // starts with the default (hidden) capability, so re-report it.
  reconnected () {
    this.probe()
  },

  destroyed () {
    this.abort()
  },

  supported () {
    return typeof self !== 'undefined' && 'LanguageModel' in self
  },

  // pushEvent REJECTS ("LiveView not connected") when mounted() runs before this
  // view's channel has joined (Brainbox drops/reconnects the socket during
  // auth). It returns a rejected promise rather than throwing, so a try/catch
  // never fires and the rejection surfaces as an unhandled rejection
  // (BRAINBOX-Q, BRAINBOX-W).
  //
  // `liveSocket.isConnected()` is not enough to gate on: it reports the
  // *transport*, while LiveView rejects on this *view's channel*, which stays
  // unjoined for a window after the transport is back. So retry off the actual
  // rejection, not just off a transport check — that both silences the
  // unhandled rejection and makes the capability report genuinely land once we
  // have rejoined.
  report (payload, attempt = 0) {
    if (this.isMounted === false) {return}

    const retry = () => {
      if (attempt < 40) {
        setTimeout(() => this.report(payload, attempt + 1), 150)
      }
    }

    if (!this.liveSocket || !this.liveSocket.isConnected()) {
      retry()
      return
    }

    try {
      const pushed = this.pushEvent('local_ai_capability', payload)
      if (pushed && typeof pushed.catch === 'function') {pushed.catch(retry)}
    } catch {
      retry()
    }
  },

  async probe () {
    if (!this.supported()) {
      this.report({ supported: false, availability: 'unsupported' })
      return
    }
    let availability
    try {
      availability = String(await self.LanguageModel.availability())
    } catch {
      availability = 'unavailable'
    }
    this.report({ supported: true, availability })
  },

  // Preferred output language for the model (silences the "no output language
  // specified" attestation warning). Fall back to English.
  outputLanguage () {
    const supported = ['de', 'en', 'es', 'fr', 'ja']
    const lang = (self.navigator?.language || 'en').split('-')[0].toLowerCase()
    return supported.includes(lang) ? lang : 'en'
  },

  // Trigger a model download without generating, streaming progress back so the
  // picker + in-conversation card can show it. Re-probes to `available` when done.
  async ensureDownloaded () {
    if (!this.supported() || this.downloading) {return}
    let availability
    try {
      availability = String(await self.LanguageModel.availability())
    } catch {
      availability = 'unavailable'
    }
    if (availability === 'available' || availability === 'unavailable') {
      this.report({ supported: true, availability })
      return
    }

    this.downloading = true
    this.report({ supported: true, availability: 'downloading' })
    try {
      const session = await self.LanguageModel.create({
        expectedOutputs: [{ type: 'text', languages: [this.outputLanguage()] }],
        monitor: (m) => {
          m.addEventListener('downloadprogress', (e) => {
            try {
              this.pushEvent('local_download_progress', { pct: Math.round((e.loaded || 0) * 100) })
            } catch {
              /* not connected yet — ignore */
            }
          })
        }
      })
      session.destroy()
      this.report({ supported: true, availability: 'available' })
    } catch {
      this.report({ supported: true, availability: 'downloadable' })
    } finally {
      this.downloading = false
    }
  },

  async run ({ message_id, messages, instruction }) {
    if (!this.supported()) {
      this.pushEvent('local_error', {
        message_id,
        reason: 'On-device AI is not available in this version of Wavebox.'
      })
      return
    }

    const list = Array.isArray(messages) ? messages : []
    // System + prior turns seed the session; the final user turn is the prompt.
    const initialPrompts = list.slice(0, -1).map((m) => ({ role: m.role, content: m.content }))
    const last = list.length ? list[list.length - 1].content : ''

    try {
      this.controller = new AbortController()

      const session = await this.createSession(initialPrompts, message_id)
      this.session = session

      const promptText = this._foldedPreamble ? `${this._foldedPreamble}\n\n${last}` : last
      this._foldedPreamble = null

      const budget = await this.measureBudget(session, promptText)

      if (budget.fits) {
        // Believed to fit — stream a single reply, but fall back to map-reduce
        // if the model rejects it anyway (measurement can be optimistic).
        try {
          await this.streamPrompt(session, promptText, message_id)
        } catch (e) {
          if (!this.isTooLarge(e)) {throw e}
          await this.mapReduce({ message_id, instruction, bulk: promptText, initialPrompts, charBudget: budget.charBudget })
        }
      } else {
        await this.mapReduce({ message_id, instruction, bulk: promptText, initialPrompts, charBudget: budget.charBudget })
      }

      this.pushEvent('local_done', { message_id })
    } catch (err) {
      if (err && err.name === 'AbortError') {
        // User cancelled — the coordinator was already stopped server-side.
        this.pushEvent('local_done', { message_id })
      } else {
        this.pushEvent('local_error', {
          message_id,
          reason: (err && err.message) || 'On-device generation failed.'
        })
      }
    } finally {
      this.cleanup()
    }
  },

  // Create a session, folding the system/history into the prompt if a build
  // rejects an initialPrompts system role. message_id scopes download progress.
  async createSession (initialPrompts, message_id) {
    const expectedOutputs = [{ type: 'text', languages: [this.outputLanguage()] }]
    const monitor = (m) => {
      m.addEventListener('downloadprogress', (e) => {
        try {
          this.pushEvent('local_download_progress', {
            message_id,
            pct: Math.round((e.loaded || 0) * 100)
          })
        } catch {
          /* not connected — ignore */
        }
      })
    }

    const opts = { expectedOutputs, monitor }
    if (initialPrompts && initialPrompts.length) {opts.initialPrompts = initialPrompts}

    try {
      return await self.LanguageModel.create(opts)
    } catch (e) {
      if (initialPrompts && initialPrompts.length) {
        this._foldedPreamble = initialPrompts.map((p) => p.content).join('\n\n')
        return await self.LanguageModel.create({ expectedOutputs, monitor })
      }
      throw e
    }
  },

  // Stream a single prompt, forwarding delta tokens (with <think> stripped).
  async streamPrompt (session, text, message_id) {
    const think = makeThinkFilter()
    let answered = false
    let raw = ''
    const stream = session.promptStreaming(text, { signal: this.controller.signal })
    for await (const chunk of stream) {
      const s = String(chunk)
      raw += s
      const visible = think(s)
      if (visible) {
        answered = true
        this.pushEvent('local_token', { message_id, chunk: visible })
      }
    }


    // If promptStreaming yielded nothing, fall back to a non-streaming prompt()
    // on a fresh session (belt-and-braces for backends where streaming misfires).
    if (!raw.trim()) {
      const s = await this.createSession([], message_id)
      try {
        raw = String(await s.prompt(text, { signal: this.controller.signal }))
      } finally {
        s.destroy()
      }
    }

    // Never render an empty reply: emit the (de-thinked) raw output if the
    // streamed/visible pass produced nothing.
    if (!answered) {
      const fallback = raw.replace(/<\/?think>/g, '').trim()
      if (fallback) {this.pushEvent('local_token', { message_id, chunk: fallback })}
    }
  },

  // True when an error looks like a context-window overflow.
  isTooLarge (e) {
    return !!e && /too large|too big|exceed|context (window|length)|quota|token limit/i.test(String((e && e.message) || e))
  },

  // Measure whether `text` fits the session, and derive a char budget for
  // chunking from the *actual* chars/token ratio of this content. Falls back to
  // conservative constants when the quota API is unavailable.
  async measureBudget (session, text) {
    const chars = String(text).length
    try {
      const quota = session.inputQuota
      const used = session.inputUsage || 0
      if (typeof quota === 'number' && quota > 0 && typeof session.measureInputUsage === 'function') {
        const cost = await session.measureInputUsage(text)
        const reserve = Math.min(OUTPUT_RESERVE_TOKENS, Math.floor(quota * 0.25))
        const usableTokens = Math.max(128, quota - used - reserve - 64) // 64 ≈ map instruction
        // chars/token from this content → chars that fit the usable budget.
        const perTok = cost > 0 ? chars / cost : 4
        const charBudget = Math.max(MIN_CHUNK_CHARS, Math.floor(usableTokens * perTok * 0.9))
        return { quota, used, cost, fits: used + cost <= quota - reserve, charBudget }
      }
    } catch {
      /* quota API unavailable — fall through */
    }
    return { quota: null, used: 0, cost: null, fits: chars <= FALLBACK_INPUT_CHARS, charBudget: CHUNK_CHARS }
  },

  async inputFits (session, text) {
    return (await this.measureBudget(session, text)).fits
  },

  // Collect a full (non-UI) completion. Prefers streaming (which the LiteRT-LM
  // backend implements) and falls back to non-streaming prompt() if streaming
  // yields nothing — used for the map step where we only need the text, not
  // token-by-token UI updates.
  async promptCollect (session, text) {
    let out = ''
    try {
      const stream = session.promptStreaming(text, { signal: this.controller.signal })
      for await (const chunk of stream) {out += String(chunk)}
    } catch (e) {
      if (this.isTooLarge(e)) {throw e}
      out = ''
    }
    if (!out.trim()) {
      out = String(await session.prompt(text, { signal: this.controller.signal }))
    }
    return out
  },

  // Token count for `text` (real measurement when available, else ~chars/4).
  async tokensOf (session, text) {
    try {
      if (typeof session.measureInputUsage === 'function') {
        return await session.measureInputUsage(text)
      }
    } catch {
      /* measurement unavailable — estimate */
    }
    return Math.round(String(text).length / 4)
  },

  // Map-reduce an oversized input: summarize each chunk, then apply the user's
  // ask to the combined notes (streamed). Chunks are split down until each one
  // is measured to fit; recurses if the combined notes are still too large.
  async mapReduce ({ message_id, instruction, bulk, initialPrompts, charBudget, tokensSoFar }) {
    const ask = (instruction && instruction.trim()) || 'Summarize the document concisely.'

    // Drop a duplicated instruction prefix so it isn't summarized as content.
    let content = String(bulk || '')
    if (instruction && content.startsWith(instruction)) {
      content = content.slice(instruction.length).trim()
    }

    const budget = charBudget || CHUNK_CHARS
    const initial = this.chunkText(content, budget)

    if (!initial.length) {
      const s = await this.createSession([], message_id)
      try {
        await this.streamPrompt(s, ask, message_id)
      } finally {
        s.destroy()
      }
      return
    }

    // Map: process a work queue, measuring each piece and halving any that don't
    // fit — so it self-corrects to whatever the real window turns out to be.
    const queue = initial.slice().reverse()
    const partials = []
    let done = 0
    // Cumulative content tokens processed, shown as progress. Carried across
    // recursive passes (when the combined notes still overflow) so the count
    // keeps climbing instead of resetting to 0 on each pass.
    let tokensRead = tokensSoFar || 0
    let truncated = false

    while (queue.length) {
      if (done >= MAX_CHUNKS) {
        truncated = true
        break
      }

      const piece = queue.pop()
      const mapPrompt =
        'The following is an excerpt from a longer document. ' +
        `Extract the key points as concise notes:\n\n${piece}`

      const s = await this.createSession([], message_id)
      try {
        if (!(await this.inputFits(s, mapPrompt)) && piece.length > MIN_CHUNK_CHARS) {
          const h = Math.floor(piece.length / 2)
          queue.push(piece.slice(h))
          queue.push(piece.slice(0, h))
          continue
        }

        const pieceTokens = await this.tokensOf(s, piece)
        const verb = PONDER_VERBS[done % PONDER_VERBS.length]
        // Prefill (reading the chunk) emits no stream, so ease the counter up
        // toward this chunk's target over the inference instead of one big jump.
        this.rampStart(message_id, verb, tokensRead, tokensRead + pieceTokens)
        try {
          const out = await this.promptCollect(s, mapPrompt)
          // Only advance the counters once the chunk actually succeeds, so a
          // too-large retry (split below) doesn't double-count or skip a verb.
          partials.push(stripThink(out))
          tokensRead += pieceTokens
          done += 1
        } finally {
          this.rampStop()
        }
        this.status(message_id, `${verb}… ${formatTokens(tokensRead)}`)
      } catch (e) {
        if (this.isTooLarge(e) && piece.length > MIN_CHUNK_CHARS) {
          const h = Math.floor(piece.length / 2)
          queue.push(piece.slice(h))
          queue.push(piece.slice(0, h))
        } else if (!this.isTooLarge(e)) {
          throw e
        }
        // else: too small to split further — skip this fragment
      } finally {
        s.destroy()
      }
    }

    const joined = partials.filter(Boolean).join('\n\n')

    // Reduce: apply the user's ask to the combined notes, streamed.
    const reduceSession = await this.createSession(initialPrompts, message_id)
    const pre = this._foldedPreamble ? `${this._foldedPreamble}\n\n` : ''
    this._foldedPreamble = null

    try {
      const reducePrompt =
        `${pre}${ask}\n\nWrite your answer using only these notes taken from across ` +
        `the whole document:\n\n${joined}`

      // If the notes themselves overflow, recurse (summarize the summaries).
      // Only show "Joining the dots…" on the final pass that actually streams —
      // otherwise the label would flash on, then snap back to "Pondering…" when
      // we recurse, which reads as a render flash between passes.
      if (await this.inputFits(reduceSession, reducePrompt)) {
        this.status(message_id, `Joining the dots… ${formatTokens(tokensRead)}`)
        await this.streamPrompt(reduceSession, reducePrompt, message_id)
      } else {
        reduceSession.destroy()
        await this.mapReduce({
          message_id,
          instruction,
          bulk: joined,
          initialPrompts,
          charBudget: budget,
          tokensSoFar: tokensRead
        })
        return
      }
    } finally {
      try {
        reduceSession.destroy()
      } catch {
        /* already destroyed on the recurse path */
      }
    }

    if (truncated) {
      this.pushEvent('local_token', {
        message_id,
        chunk: `\n\n_(The content was very long; summarized the first ${MAX_CHUNKS} sections.)_`
      })
    }
  },

  // Split text into ~budget-char pieces on paragraph boundaries, hard-splitting
  // any single paragraph that's itself too big.
  chunkText (content, budget) {
    const limit = budget || CHUNK_CHARS
    const text = String(content || '').trim()
    if (!text) {return []}

    const chunks = []
    let cur = ''
    for (const para of text.split(/\n\s*\n/)) {
      const segments = para.length > limit ? this.hardSplit(para, limit) : [para]
      for (const seg of segments) {
        if (cur && cur.length + seg.length + 2 > limit) {
          chunks.push(cur)
          cur = seg
        } else {
          cur = cur ? `${cur}\n\n${seg}` : seg
        }
      }
    }
    if (cur) {chunks.push(cur)}
    return chunks
  },

  hardSplit (s, budget) {
    const limit = budget || CHUNK_CHARS
    const out = []
    for (let i = 0; i < s.length; i += limit) {out.push(s.slice(i, i + limit))}
    return out
  },

  // Push a transient progress line (rendered in the loading indicator).
  status (message_id, text) {
    try {
      this.pushEvent('local_status', { message_id, status: text })
    } catch {
      /* not connected — ignore */
    }
  },

  // Ease the token counter from `from` toward `to` (~4–5 updates/sec, ease-out)
  // so it climbs during a chunk's inference instead of jumping at the end.
  rampStart (message_id, verb, from, to) {
    this.rampStop()
    let shown = from
    this.status(message_id, `${verb}… ${formatTokens(shown)}`)
    if (to <= from) {return}
    this._ticker = setInterval(() => {
      if (shown >= to) {return}
      shown += Math.max(1, Math.ceil((to - shown) * 0.2))
      if (shown > to) {shown = to}
      this.status(message_id, `${verb}… ${formatTokens(shown)}`)
    }, 220)
  },

  rampStop () {
    if (this._ticker) {
      clearInterval(this._ticker)
      this._ticker = null
    }
  },

  abort () {
    try {
      if (this.controller) {this.controller.abort()}
    } catch {
      /* no-op */
    }
    this.cleanup()
  },

  cleanup () {
    this.rampStop()
    if (this.session) {
      try {
        this.session.destroy()
      } catch {
        /* no-op */
      }
      this.session = null
    }
    this.controller = null
  }
}

export default LocalModel
